import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { login, verifyToken, refreshToken, getCurrentUser, setupTokenRefresh, getStoredUserData, getTokens } from '../services/auth';
import { clearAllSavedFilterStates } from '../utils/filterStorage';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refreshUserData = useCallback(async () => {
        // Fetch fresh user data from /api/users/me/ so that any role or permission
        // changes made by an admin are reflected immediately without requiring re-login.
        try {
            const freshUserData = await getCurrentUser();
            if (freshUserData && freshUserData.role && freshUserData.role.permissions) {
                setUser(freshUserData);
                return freshUserData;
            }
            // If API returns data without the expected shape, fall back to localStorage
            const storedUserData = getStoredUserData();
            if (storedUserData && storedUserData.role && storedUserData.role.permissions) {
                setUser(storedUserData);
                return storedUserData;
            }
            throw new Error('No valid user data available');
        } catch (error) {
            console.error('Failed to refresh user data from API, falling back to localStorage:', error);
            const storedUserData = getStoredUserData();
            if (storedUserData && storedUserData.role && storedUserData.role.permissions) {
                setUser(storedUserData);
                return storedUserData;
            }
            throw error;
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            // Call backend logout endpoint to clear last_activity and free active session quota
            const { getTokens } = await import('../services/auth');
            const { accessToken } = getTokens();
            if (accessToken) {
                await fetch('/api/users/logout/', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    }
                });
            }
        } catch (error) {
            console.error('Logout API call failed:', error);
        } finally {
            // Clear local session regardless of API success
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('userData');
            clearAllSavedFilterStates();
            if (typeof sessionStorage !== 'undefined') {
                sessionStorage.clear();
            }
            setIsAuthenticated(false);
            setUser(null);
        }
    }, []);

    // Check authentication status on app load
    useEffect(() => {
        const checkAuth = async () => {
            try {
                setLoading(true);
                const storedUserData = getStoredUserData();
                const { accessToken, refreshToken: storedRefreshToken } = getTokens();

                if (accessToken && storedUserData) {
                    // Verify the token is still valid
                    try {
                        await verifyToken(accessToken);

                        // Token is valid — seed from localStorage immediately so the UI
                        // is responsive, then refresh permissions from the API in the
                        // background so any role/permission changes are picked up without
                        // requiring a re-login.
                        setIsAuthenticated(true);
                        setUser(storedUserData);
                        setupTokenRefresh();

                        refreshUserData().catch(() => {});
                    } catch {
                        // Token is invalid, try to refresh it
                        try {
                            const refreshResponse = await refreshToken();
                            if (refreshResponse.access) {
                                const freshUserData = await refreshUserData();
                                setIsAuthenticated(true);
                                setUser(freshUserData);
                                setupTokenRefresh();
                            } else {
                                throw new Error('No access token in refresh response');
                            }
                        } catch {
                            logout();
                        }
                    }
                } else if (storedRefreshToken) {
                    try {
                        const refreshResponse = await refreshToken();
                        if (refreshResponse.access) {
                            const freshUserData = await refreshUserData();
                            setIsAuthenticated(true);
                            setUser(freshUserData);
                            setupTokenRefresh();
                        } else {
                            throw new Error('No access token in refresh response');
                        }
                    } catch {
                        logout();
                    }
                } else {
                    setIsAuthenticated(false);
                    setUser(null);
                }
            } catch (error) {
                console.error('Auth check failed:', error);
                logout();
            } finally {
                setLoading(false);
            }
        };

        checkAuth();
    }, [refreshUserData, logout]);

    const loginUser = useCallback(async (credentials) => {
        try {
            const response = await login(credentials);
            if (response.user && response.user.role && response.user.role.permissions) {
                setIsAuthenticated(true);
                setUser(response.user);
                setupTokenRefresh();
                return response;
            } else {
                throw new Error('Invalid user data structure');
            }
        } catch (error) {
            throw error;
        }
    }, []);

    // Token refresh is handled by setupTokenRefresh() in services/auth.js (setTimeout approach).
    // No additional polling interval needed here.

    // Memoize so consumers only re-render when these specific values change,
    // not on every AuthProvider render cycle.
    const value = useMemo(() => ({
        isAuthenticated,
        user,
        loading,
        login: loginUser,
        logout,
        refreshUserData,
    }), [isAuthenticated, user, loading, loginUser, logout, refreshUserData]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 